import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import "@/App.css";
import { LanguageProvider } from "@/lib/i18n";
import { Navbar } from "@/components/site/Navbar";
import { Footer, WhatsAppFloat } from "@/components/site/Footer";
import { PageTransition } from "@/components/site/Motion";
import Home from "@/pages/Home";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import Sourcing from "@/pages/Sourcing";
import Sell from "@/pages/Sell";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import { Legal, Privacy } from "@/pages/Legal";
import { AdminProvider, AdminLogin, ForgotPassword, ResetPassword, RequireAdmin } from "@/admin/AdminAuth";
import { AdminLayout } from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import VehiclesAdmin from "@/admin/VehiclesAdmin";
import VehicleForm from "@/admin/VehicleForm";
import Leads from "@/admin/Leads";
import Media from "@/admin/Media";
import HomepageContent from "@/admin/HomepageContent";
import Settings from "@/admin/Settings";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const Site = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
    <WhatsAppFloat />
  </>
);

const PublicRoutes = () => {
  const location = useLocation();
  return (
    <Site>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Home /></PageTransition>} />
          <Route path="/vehicles" element={<PageTransition><Vehicles /></PageTransition>} />
          <Route path="/vehicles/:slug" element={<PageTransition><VehicleDetail /></PageTransition>} />
          <Route path="/sourcing" element={<PageTransition><Sourcing /></PageTransition>} />
          <Route path="/sell" element={<PageTransition><Sell /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </Site>
  );
};

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AdminProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<Dashboard />} />
              <Route path="vehicles" element={<VehiclesAdmin />} />
              <Route path="vehicles/new" element={<VehicleForm />} />
              <Route path="vehicles/:id" element={<VehicleForm />} />
              <Route path="leads/:type" element={<Leads />} />
              <Route path="media" element={<Media />} />
              <Route path="homepage" element={<HomepageContent />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/*" element={<PublicRoutes />} />
          </Routes>
        </AdminProvider>
        <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#14171c", border: "1px solid rgba(255,255,255,.1)", color: "#f4f5f7" } }} />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
